import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import apiClient from "../api/apiClient";
import { useAuth } from "../auth/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";

const GOALS = ["Aesthetics and lean muscle", "Strength and power", "Mobility", "Energy and overall performance"];
const TIMES = ["2-3 hours per week", "3-5 hours per week", "5+ hours per week"];
const METHODS = ["Email", "Mobile call", "WhatsApp", "Text message"];
// Values are what the API stores; labels avoid "Home", which already
// translates as the site's home page.
const LOCATIONS = [
  { value: "gym", label: "At the gym" },
  { value: "home", label: "At home" },
  { value: "other", label: "Somewhere else" },
];
const EMPTY = {
  professional_role: "", primary_goal: "", time_commitment: "",
  training_location: "", training_location_other: "", home_equipment: "", message: "",
  mobile: "", preferred_contact_method: "", ready_to_commit: false,
};

const PAYMENT_POLL_MS = 5000;
const MAX_PAYMENT_POLLS = 12;

const ProgramInquiryForm = () => {
  const { t } = useLanguage();
  const { user, loading: authLoading } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [programRequest, setProgramRequest] = useState(undefined);
  const [ultimatePackage, setUltimatePackage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(false);
  const [paymentError, setPaymentError] = useState("");

  /* Hands the member to Whish; settlement is what approves their request. */
  const payForProgram = async () => {
    if (!ultimatePackage) return;

    setPaying(true);
    setPaymentError("");

    try {
      const { data } = await apiClient.post("/payments/initiate", {
        package_id: ultimatePackage.id,
      });

      const collectUrl = data.data?.collect_url;

      if (!collectUrl) throw new Error("missing collect url");

      window.location.assign(collectUrl);
    } catch (paymentException) {
      const errors = paymentException.response?.data?.errors;
      setPaymentError(
        errors
          ? Object.values(errors).flat().join(" ")
          : paymentException.response?.data?.message || t("Could not start the payment. Please try again."),
      );
      setPaying(false);
    }
  };

  useEffect(() => {
    if (!user) {
      setProgramRequest(null);
      return undefined;
    }

    let cancelled = false;
    let timer;
    let polls = 0;

    const load = () => apiClient.get("/program-request")
      .then(({ data }) => {
        if (cancelled) return;

        const next = data.request ?? null;
        setProgramRequest(next);
        setUltimatePackage(data.ultimate_package ?? null);
        setForm((current) => ({ ...current, mobile: user.phone || current.mobile }));

        // The server re-checks with Whish on every call, so a payment that is
        // still clearing resolves itself here instead of leaving the member
        // to wonder whether to pay again.
        if (next?.payment?.state === "processing" && polls < MAX_PAYMENT_POLLS) {
          polls += 1;
          timer = window.setTimeout(load, PAYMENT_POLL_MS);
        }
      })
      .catch((requestError) => {
        if (cancelled) return;
        setError(requestError.response?.data?.message || t("Could not load your program request."));
        setProgramRequest(null);
      });

    load();

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [user, t]);

  const set = (field) => (event) => setForm((current) => ({
    ...current,
    [field]: event.target.type === "checkbox" ? event.target.checked : event.target.value,
  }));

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      const { data } = await apiClient.post("/program-request", form);
      setProgramRequest(data.request);
    } catch (requestError) {
      const errors = requestError.response?.data?.errors;
      setError(errors ? Object.values(errors).flat().join(" ") : requestError.response?.data?.message || t("Could not submit your request."));
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || programRequest === undefined) {
    return <div className="text-center py-5"><div className="spinner-border text-danger" role="status" /></div>;
  }

  if (!user) {
    return (
      <div className="hpa-program-inquiry-success text-center">
        <i className="fa-solid fa-lock" aria-hidden="true" />
        <h3>{t("Login Required")}</h3>
        <p>{t("Please log in to request your personalized Ultimate Program.")}</p>
        <Link to="/auth?redirect=program-inquiry" className="btn btn-primary btn-skew"><span>{t("Login")}</span></Link>
      </div>
    );
  }

  if (programRequest) {
    const payment = programRequest.payment || { state: "none" };

    /*
     * Awaiting payment is not one state but four, and the member is told which
     * one they are in: never started, still clearing, did not go through, or
     * refunded. Each carries the action that matches it.
     */
    const awaitingCopy = {
      none: [
        "fa-clock",
        "Your details are saved",
        "Complete the payment to unlock your program. Your coach starts building it as soon as the payment clears.",
      ],
      processing: [
        "fa-hourglass-half",
        "Your payment is still clearing",
        "We are waiting on confirmation from Whish. This page updates on its own. Nothing more is needed from you right now.",
      ],
      failed: [
        "fa-circle-exclamation",
        "Your last payment did not go through",
        "Nothing was charged. Your answers are saved, so you only need to complete the payment.",
      ],
      refunded: [
        "fa-rotate-left",
        "That payment was refunded",
        "Your answers are saved. Pay again whenever you are ready, or contact the academy team if this was not expected.",
      ],
    };

    const statusCopy = {
      approved: ["fa-circle-check", "Program approved", programRequest.program_ready
        ? "Your personalized program and all exercise libraries are now available."
        : "You now have access to all exercise libraries. Your coach is preparing your personalized program."],
      rejected: ["fa-circle-info", "Request reviewed", programRequest.admin_notes || "Please contact the coaching team for more information."],
    };

    const [icon, title, description] = programRequest.status === "submitted"
      ? (awaitingCopy[payment.state] || awaitingCopy.none)
      : (statusCopy[programRequest.status] || awaitingCopy.none);

    const canPay = programRequest.status === "submitted" && payment.state !== "processing";
    const payLabel = payment.state === "failed" || payment.state === "refunded"
      ? t("Try the payment again")
      : ultimatePackage
        ? t("Pay {price} {currency}", { price: ultimatePackage.price, currency: ultimatePackage.currency })
        : t("Continue to Payment");

    return (
      <div className="hpa-program-inquiry-success text-center">
        <i className={`fa-solid ${icon}`} aria-hidden="true" />
        <h3>{t(title)}</h3>
        <p>{t(description)}</p>

        {payment.reference && (
          <p className="hpa-program-inquiry-reference">
            {t("Payment reference")}: <strong>{payment.reference}</strong>
          </p>
        )}

        {paymentError && <div className="alert alert-danger" role="alert">{paymentError}</div>}

        {canPay && (
          <button type="button" className="btn btn-primary btn-skew" onClick={payForProgram} disabled={paying}>
            <span>{paying ? t("Preparing payment…") : payLabel}</span>
          </button>
        )}

        {payment.state === "processing" && payment.resume_url && (
          <a href={payment.resume_url} className="btn btn-primary btn-skew">
            <span>{t("Finish paying")}</span>
          </a>
        )}

        {programRequest.status === "approved" && (
          <Link to="/my-program" className="btn btn-primary btn-skew"><span>{t("Open My Program")}</span></Link>
        )}
      </div>
    );
  }

  return (
    <>
      {ultimatePackage && (
        <div className="hpa-ultimate-package-summary mb-4">
          <span className="sub-title">{t("Ultimate Program")}</span>
          <h3>{ultimatePackage.name}</h3>
          <p>{t("Includes a coach-built personal program and access to every exercise library.")}</p>
          <strong>{ultimatePackage.price} {ultimatePackage.currency}</strong>
          <small className="d-block mt-2">{t("Tell us about your training first. You pay on the next step, and your program is unlocked as soon as the payment clears.")}</small>
        </div>
      )}

      {error && <div className="alert alert-danger" role="alert">{error}</div>}

      <form className="hpa-contact-form hpa-program-inquiry-form" onSubmit={submit}>
        <div className="row">
          <div className="col-md-6 mb-3">
            <label className="form-label text-primary fw-semibold">{t("Account")}</label>
            <input className="form-control" value={`${user.name} (${user.email})`} disabled />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label text-primary fw-semibold">{t("Professional Role / Industry")}</label>
            <input className="form-control" value={form.professional_role} onChange={set("professional_role")} required />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label text-primary fw-semibold">{t("Primary Goal")}</label>
            <select className="form-control" value={form.primary_goal} onChange={set("primary_goal")} required>
              <option value="">{t("Select your goal")}</option>
              {GOALS.map((goal) => <option key={goal} value={goal}>{t(goal)}</option>)}
            </select>
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label text-primary fw-semibold">{t("Time Commitment")}</label>
            <select className="form-control" value={form.time_commitment} onChange={set("time_commitment")} required>
              <option value="">{t("Select weekly time")}</option>
              {TIMES.map((time) => <option key={time} value={time}>{t(time)}</option>)}
            </select>
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label text-primary fw-semibold">{t("Where do you want to train?")}</label>
            <select className="form-control" value={form.training_location} onChange={set("training_location")} required>
              <option value="">{t("Select where you train")}</option>
              {LOCATIONS.map(({ value, label }) => <option key={value} value={value}>{t(label)}</option>)}
            </select>
          </div>
          {form.training_location === "other" && (
            <div className="col-md-6 mb-3">
              <label className="form-label text-primary fw-semibold">{t("Where exactly?")}</label>
              <input
                className="form-control"
                value={form.training_location_other}
                onChange={set("training_location_other")}
                placeholder={t("e.g. outdoor park, office gym, hotel gym")}
                maxLength={255}
                required
              />
            </div>
          )}
          {form.training_location === "home" && (
            <div className="col-12 mb-3">
              <label className="form-label text-primary fw-semibold">{t("What equipment do you have at home?")}</label>
              <textarea
                className="form-control"
                rows={3}
                value={form.home_equipment}
                onChange={set("home_equipment")}
                placeholder={t("e.g. dumbbells (up to 20 kg), resistance bands, pull-up bar, or none")}
                maxLength={2000}
                required
              />
            </div>
          )}
          <div className="col-12 mb-3">
            <label className="form-label text-primary fw-semibold">{t("Goals, experience, limitations, and why you are applying")}</label>
            <textarea className="form-control" rows={5} value={form.message} onChange={set("message")} required />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label text-primary fw-semibold">{t("Mobile Number")}</label>
            <input type="tel" className="form-control" value={form.mobile} onChange={set("mobile")} required />
          </div>
          <div className="col-md-6 mb-3">
            <label className="form-label text-primary fw-semibold">{t("Preferred Contact Method")}</label>
            <select className="form-control" value={form.preferred_contact_method} onChange={set("preferred_contact_method")} required>
              <option value="">{t("Choose a method")}</option>
              {METHODS.map((method) => <option key={method} value={method}>{t(method)}</option>)}
            </select>
          </div>
          <div className="col-12 mb-4">
            <label className="contact-commit-check">
              <input type="checkbox" checked={form.ready_to_commit} onChange={set("ready_to_commit")} required />
              <span>{t("I am ready to commit to a premium results-driven coaching program.")}</span>
            </label>
          </div>
        </div>
        <button type="submit" className="btn btn-primary btn-skew" disabled={submitting || !ultimatePackage?.available}>
          <span>{submitting ? t("Submitting…") : t("Request Ultimate Program")}</span>
        </button>
      </form>
    </>
  );
};

export default ProgramInquiryForm;
